import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, Send, Video } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data as fmtData, dataHora } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE } from './dados';

/** Sala própria com gravação e transcrição (20/10): a "pasta" da reunião na ficha do agendamento. */
interface Gravacao {
  id: string;
  inicio: string;
  duracao_s: number;
  estado: 'pronta' | 'expirada';
  expira_em: string;
  arquivos: { nome: string; tamanho: number }[];
  transcricao: 'pronta' | 'falhou';
  transcricao_anexo_id: string | null;
  enviada_em: string | null;
}

const minutos = (s: number) => Math.max(1, Math.round(s / 60));

function Enviar({ g, aoFechar }: { g: Gravacao; aoFechar: () => void }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [cliente, setCliente] = useState(true);
  const [convidados, setConvidados] = useState(true);
  const [portal, setPortal] = useState(false);
  const [emails, setEmails] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const enviar = useMutation({
    mutationFn: () =>
      post<{ enviados: number }>(`/agenda/gravacoes/${g.id}/enviar`, {
        cliente,
        convidados,
        portal,
        emails: emails.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean),
      }),
    onMutate: () => setErro(null),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: [CHAVE] });
      await qc.invalidateQueries({ queryKey: ['chatter', 'agenda.agendamento'] });
      toast.sucesso(t('gravacoes.enviado', { count: r.enviados }));
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('gravacoes.enviarTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={enviar.isPending} onClick={() => enviar.mutate()}>
            {t('gravacoes.enviar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-perigo">
            {erro}
          </p>
        )}
        <p className="text-tinta-2">{t('gravacoes.enviarTexto')}</p>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={cliente} onChange={(e) => setCliente(e.target.checked)} />
          {t('gravacoes.paraCliente')}
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={convidados} onChange={(e) => setConvidados(e.target.checked)} />
          {t('gravacoes.paraConvidados')}
        </label>
        {s.pode('portal.publicar') && g.transcricao_anexo_id && (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={portal} onChange={(e) => setPortal(e.target.checked)} />
            {t('gravacoes.paraPortal')}
          </label>
        )}
        <Rotulo id="grav-emails" rotulo={t('gravacoes.outros')} ajuda={t('gravacoes.outrosAjuda')}>
          <Entrada id="grav-emails" type="text" inputMode="email" value={emails} maxLength={2000} onChange={(e) => setEmails(e.target.value)} />
        </Rotulo>
      </div>
    </Modal>
  );
}

export function Gravacoes({ agendamentoId }: { agendamentoId: string }) {
  const { t } = useTranslation('agenda');
  const s = useSessao();
  const toast = useToast();
  const [enviando, setEnviando] = useState<Gravacao | null>(null);
  const q = useQuery({ queryKey: [CHAVE, 'gravacoes', agendamentoId], queryFn: () => get<Gravacao[]>(`/agenda/agendamentos/${agendamentoId}/gravacoes`) });
  const abrir = useMutation({
    mutationFn: (g: Gravacao) => post<{ url: string }>(`/agenda/gravacoes/${g.id}/link`, { arquivo: 'gravacao.mp4' }),
    onSuccess: (r) => window.open(r.url, '_blank', 'noopener,noreferrer'),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const lista = q.data ?? [];
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-borda bg-sup-2 p-4">
      <h3 className="text-sm font-semibold">{t('gravacoes.titulo')}</h3>
      {q.isError && <p className="text-sm text-perigo">{mensagemDeErro(q.error)}</p>}
      {!q.isError && !q.isLoading && lista.length === 0 && <p className="text-sm text-tinta-3">{t('gravacoes.vazio')}</p>}
      {lista.map((g) => {
        const temVideo = g.arquivos.some((a) => a.nome === 'gravacao.mp4');
        return (
          <div key={g.id} className="flex flex-col gap-2 border-t border-borda pt-3 first-of-type:border-t-0 first-of-type:pt-0">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="numero font-medium">{dataHora(g.inicio)}</span>
              <span className="numero text-tinta-3">{t('gravacoes.duracao', { n: minutos(g.duracao_s) })}</span>
              {g.estado === 'expirada' ? <Selo>{t('gravacoes.expirada')}</Selo> : <Selo cor="verde">{t('gravacoes.disponivel', { ate: fmtData(g.expira_em) })}</Selo>}
              {g.transcricao === 'falhou' && <Selo cor="laranja">{t('gravacoes.semTranscricao')}</Selo>}
              {g.enviada_em && <Selo cor="ciano">{t('gravacoes.enviada', { em: fmtData(g.enviada_em) })}</Selo>}
            </div>
            <div className="flex flex-wrap gap-2">
              {g.estado === 'pronta' && temVideo && (
                <Botao tamanho="sm" icone={<Video className="size-4" aria-hidden />} carregando={abrir.isPending && abrir.variables?.id === g.id} onClick={() => abrir.mutate(g)}>
                  {t('gravacoes.assistir')}
                </Botao>
              )}
              {g.transcricao_anexo_id && (
                <a href={`/api/v1/anexos/${g.transcricao_anexo_id}`} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-tinta-2 hover:bg-sup-3 hover:text-tinta-1">
                  <FileText className="size-4" aria-hidden />
                  {t('gravacoes.transcricao')}
                </a>
              )}
              {g.estado === 'pronta' && s.pode('agenda.editar') && (
                <Botao tamanho="sm" variante="fantasma" icone={<Send className="size-4" aria-hidden />} onClick={() => setEnviando(g)}>
                  {t('gravacoes.enviarParticipantes')}
                </Botao>
              )}
            </div>
          </div>
        );
      })}
      <p className="text-xs text-tinta-3">{t('gravacoes.lgpd')}</p>
      {enviando && <Enviar g={enviando} aoFechar={() => setEnviando(null)} />}
    </section>
  );
}
